<template>
  <RenameJobFamilyModal
    :show="isRename"
    :current-name="rowAction?.name || ''"
    :node-label="renameNodeLabel"
    :loading="editActionLoading"
    @close="closeRowAction"
    @confirm="handleConfirmRename"
  />

  <MoveJobFamilyModal
    :show="isMove"
    :groups="moveGroups"
    :profile-count="rowAction?.profileCount || 0"
    :draft-count="rowAction?.draftCount || 0"
    :loading="editActionLoading"
    @close="closeRowAction"
    @confirm="handleConfirmMove"
  />

  <DeleteJobFamilyModal
    :show="isDelete"
    :family-name="rowAction?.name || ''"
    :profile-count="rowAction?.profileCount || 0"
    :draft-count="rowAction?.draftCount || 0"
    :loading="editActionLoading"
    @close="closeRowAction"
    @confirm="handleConfirmDelete"
  />

  <AddJobFamilyGroupModal
    :show="isAddGroup"
    :existing-names="addGroupExistingNames"
    :loading="editActionLoading"
    @close="closeRowAction"
    @confirm="handleConfirmAddGroup"
  />

  <AddJobFamilyModal
    :show="isAddFamily"
    :existing-names="addFamilyExistingNames"
    :loading="editActionLoading"
    @close="closeRowAction"
    @confirm="handleConfirmAddFamily"
  />

  <UpdateJobFamilyModal
    :show="isMoveProfile"
    :count="moveProfileCount"
    :draft-count="moveProfileDraftCount"
    :loading="editActionLoading"
    :job-families="jobFamilies"
    :job-family-groups="jobFamilyGroups"
    @close="closeRowAction"
    @confirm="handleConfirmMoveProfile"
  />

  <RemoveFromJobFamilyModal
    :show="isRemoveProfile"
    :count="moveProfileCount"
    :draft-count="moveProfileDraftCount"
    :family-scope="removeProfileFamilyScope"
    :loading="editActionLoading"
    @close="closeRowAction"
    @confirm="handleConfirmRemoveProfile"
  />
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import RenameJobFamilyModal from '@/components/jobArchitecture/RenameJobFamilyModal.vue';
import MoveJobFamilyModal from '@/components/jobArchitecture/MoveJobFamilyModal.vue';
import DeleteJobFamilyModal from '@/components/jobArchitecture/DeleteJobFamilyModal.vue';
import AddJobFamilyModal from '@/components/jobArchitecture/AddJobFamilyModal.vue';
import AddJobFamilyGroupModal from '@/components/jobArchitecture/AddJobFamilyGroupModal.vue';
import UpdateJobFamilyModal from '@/components/jobArchitecture/UpdateJobFamilyModal.vue';
import RemoveFromJobFamilyModal from '@/components/jobArchitecture/RemoveFromJobFamilyModal.vue';
import {
  formatFamilyScope,
  getFamiliesForProfiles,
  getSelectionUsageCounts,
} from '@/components/jobArchitecture/helpers';

const jobArchitectureStore = useJobArchitecture();
const {
  rowAction,
  editActionLoading,
  treeData,
  jobFamilies,
  jobFamilyGroups,
} = storeToRefs(jobArchitectureStore);
const {
  closeRowAction,
  renameGroup,
  renameFamily,
  moveFamily,
  deleteFamily,
  addGroup,
  addFamily,
  updateSelectedProfilesFamily,
  removeSelectedProfilesFromFamily,
} = jobArchitectureStore;

const isRename = computed(() => (
  rowAction.value?.type === 'rename-family' || rowAction.value?.type === 'rename-group'
));

const isMove = computed(() => rowAction.value?.type === 'move-family');

const isDelete = computed(() => rowAction.value?.type === 'delete-family');

const isAddGroup = computed(() => rowAction.value?.type === 'add-group');

const isAddFamily = computed(() => rowAction.value?.type === 'add-family');

const isMoveProfile = computed(() => rowAction.value?.type === 'move-profile');

const isRemoveProfile = computed(() => rowAction.value?.type === 'remove-profile');

const removeProfileFamilyScope = computed(() => {
  const ulid = rowAction.value?.ulid;

  if (!ulid) {
    return '';
  }

  const families = getFamiliesForProfiles(treeData.value, [ulid]);

  return formatFamilyScope(families);
});

const moveProfileUsage = computed(() => {
  const ulid = rowAction.value?.ulid;

  if (!ulid) {
    return { profileCount: 1, draftCount: 0 };
  }

  return getSelectionUsageCounts(treeData.value, [ulid]);
});

const moveProfileCount = computed(() => moveProfileUsage.value.profileCount);
const moveProfileDraftCount = computed(() => moveProfileUsage.value.draftCount);

const addGroupExistingNames = computed(() => (
  (treeData.value?.groups || []).map((group) => group.name).filter(Boolean)
));

const addFamilyExistingNames = computed(() => {
  const groupUlid = rowAction.value?.groupUlid;
  const group = (treeData.value?.groups || []).find((item) => item.ulid === groupUlid);

  return (group?.families || []).map((family) => family.name).filter(Boolean);
});

const renameNodeLabel = computed(() => (
  rowAction.value?.type === 'rename-group' ? 'Job Family Group' : 'Job Family'
));

const moveGroups = computed(() => {
  const currentGroupUlid = rowAction.value?.groupUlid;

  return (treeData.value?.groups || []).filter((group) => (
    group?.ulid && group.ulid !== currentGroupUlid
  ));
});

const handleConfirmRename = async (newName) => {
  const { type, ulid, name } = rowAction.value;
  const action = type === 'rename-group' ? renameGroup : renameFamily;

  await action({ ulid, oldName: name, newName });
};

const handleConfirmMove = async ({ toGroupUlid, toGroupName }) => {
  await moveFamily({
    familyUlid: rowAction.value.ulid,
    fromGroupUlid: rowAction.value.groupUlid,
    toGroupUlid,
    toGroupName,
  });
};

const handleConfirmDelete = async () => {
  await deleteFamily({
    ulid: rowAction.value.ulid,
    name: rowAction.value.name,
  });
};

const handleConfirmAddGroup = async (name) => {
  await addGroup({ name });
};

const handleConfirmAddFamily = async (name) => {
  await addFamily({
    name,
    groupUlid: rowAction.value.groupUlid,
  });
};

const handleConfirmMoveProfile = async ({ familyUlid, familyName }) => {
  await updateSelectedProfilesFamily({
    familyUlid,
    familyName,
    ulids: [rowAction.value.ulid],
  });
};

const handleConfirmRemoveProfile = async () => {
  await removeSelectedProfilesFromFamily({
    ulids: [rowAction.value.ulid],
  });
};
</script>
